import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import "./adminTour.css";

const TOUR_STORAGE_KEY = "coc_admin_tour_v1";
const SCROLL_SETTLE_MS = 700;

const PAGE_STEPS = {
  "/admin/rules": [
    {
      selector: '[data-tour="rules-colours-link"]',
      title: "Colours & situations",
      body: "Open this to manage up to 9 colours and the situations/questions players answer.",
    },
    {
      selector: '[data-tour="rules-game-settings"]',
      title: "Game settings",
      body: "Set the game title and sub title shown on the player landing and game screens.",
    },
    {
      selector: '[data-tour="rules-colors"]',
      title: "Theme colours",
      body: "Adjust text, button, and popup colours used across the player experience.",
    },
    {
      selector: '[data-tour="rules-save"]',
      title: "Save",
      body: "Save all game settings and colour changes for the current theme.",
    },
  ],
  "/admin/customize-colours": [
    {
      selector: '[data-tour="coc-colors"]',
      title: "Game colours",
      body: "Add or edit up to 9 colours. Each colour’s value becomes a selectable answer in situations.",
    },
    {
      selector: '[data-tour="coc-why-toggle"]',
      title: "Why these answers fit",
      body: "When enabled, players see an explanation popup after submitting a situation.",
    },
    {
      selector: '[data-tour="coc-questions"]',
      title: "Situations / questions",
      body: "Create situations with 1–3 correct colour values. Use Prev/Next to browse pages of questions.",
    },
  ],
};

function resolveSteps(pathname) {
  const cleaned =
    pathname.endsWith("/") && pathname !== "/" ? pathname.slice(0, -1) : pathname;
  return PAGE_STEPS[cleaned] || [];
}

function isRulesPage(pathname) {
  const cleaned =
    pathname.endsWith("/") && pathname !== "/" ? pathname.slice(0, -1) : pathname;
  return cleaned === "/admin/rules";
}

export default function AdminTour() {
  const location = useLocation();
  const steps = useMemo(() => resolveSteps(location.pathname), [location.pathname]);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const scrollingRef = useRef(false);
  const settleTimerRef = useRef(null);
  const rafRef = useRef(null);

  const step = steps[index] || null;

  const readRect = useCallback(() => {
    if (!step?.selector) {
      setRect(null);
      return null;
    }
    const el = document.querySelector(step.selector);
    if (!el) {
      setRect(null);
      return null;
    }
    const r = el.getBoundingClientRect();
    const next = {
      top: r.top,
      left: r.left,
      width: Math.max(r.width, 40),
      height: Math.max(r.height, 40),
    };
    setRect(next);
    return { el, next };
  }, [step]);

  const stopTracking = useCallback(() => {
    scrollingRef.current = false;
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const trackDuringScroll = useCallback(() => {
    const tick = () => {
      if (!scrollingRef.current) return;
      readRect();
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [readRect]);

  const measure = useCallback(() => {
    const result = readRect();
    if (!result?.el) return;
    stopTracking();
    scrollingRef.current = true;
    trackDuringScroll();
    result.el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    settleTimerRef.current = window.setTimeout(() => {
      readRect();
      scrollingRef.current = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      window.setTimeout(() => readRect(), 80);
    }, SCROLL_SETTLE_MS);
  }, [readRect, stopTracking, trackDuringScroll]);

  useEffect(() => {
    if (!open) {
      stopTracking();
      return undefined;
    }
    const t = setTimeout(measure, 60);
    const onResize = () => {
      if (!scrollingRef.current) readRect();
    };
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
      stopTracking();
    };
  }, [open, measure, readRect, index, stopTracking]);

  useEffect(() => {
    setOpen(false);
    setIndex(0);
    if (!isRulesPage(location.pathname)) return undefined;
    try {
      const seen = localStorage.getItem(TOUR_STORAGE_KEY);
      if (!seen && resolveSteps(location.pathname).length) {
        const t = setTimeout(() => setOpen(true), 700);
        return () => clearTimeout(t);
      }
    } catch {
      /* ignore */
    }
    return undefined;
  }, [location.pathname]);

  const close = (persist = true) => {
    setOpen(false);
    stopTracking();
    if (persist) {
      try {
        localStorage.setItem(TOUR_STORAGE_KEY, "1");
      } catch {
        /* ignore */
      }
    }
  };

  const next = () => {
    if (index >= steps.length - 1) {
      close(true);
      return;
    }
    setIndex((i) => i + 1);
  };

  const prev = () => setIndex((i) => Math.max(0, i - 1));

  const startTour = () => {
    if (!steps.length) return;
    setIndex(0);
    setOpen(true);
  };

  let tipStyle = { top: "28%", left: "50%", transform: "translateX(-50%)" };
  if (rect) {
    const cardH = 200;
    const gap = 16;
    const below = rect.top + rect.height + gap;
    const above = rect.top - cardH - gap;
    const top =
      below + cardH < window.innerHeight - 16 ? below : Math.max(16, above);
    tipStyle = {
      top,
      left: Math.min(Math.max(16, rect.left), window.innerWidth - 340),
    };
  }

  return (
    <>
      <button
        type="button"
        className="admin-tour-trigger"
        onClick={startTour}
        disabled={!steps.length}
        title={steps.length ? "Start tour" : "No tour on this page"}
      >
        <i className="fa-solid fa-circle-question" aria-hidden /> Tour
      </button>

      {open && step ? (
        <div className="admin-tour-root" role="dialog" aria-modal="true" aria-label="Admin tour">
          <div className="admin-tour-backdrop" onClick={() => close(true)} />
          {rect ? (
            <div
              className="admin-tour-spotlight"
              style={{
                top: rect.top - 8,
                left: rect.left - 8,
                width: rect.width + 16,
                height: rect.height + 16,
              }}
            />
          ) : null}
          <div className="admin-tour-card" style={tipStyle}>
            <div className="admin-tour-stepcount">
              {index + 1} / {steps.length}
            </div>
            <h3>{step.title}</h3>
            <p>{step.body}</p>
            {!rect ? (
              <p className="admin-tour-missing">
                This section is not visible yet — scroll or expand it, then click Next.
              </p>
            ) : null}
            <div className="admin-tour-actions">
              <button type="button" className="admin-tour-skip" onClick={() => close(true)}>
                Skip
              </button>
              <div className="admin-tour-nav">
                <button type="button" onClick={prev} disabled={index === 0}>
                  Back
                </button>
                <button type="button" className="admin-tour-next" onClick={next}>
                  {index >= steps.length - 1 ? "Done" : "Next"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
